import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getUserSettings,
  updateUserSettings,
  updateTheme,
  updateNotificationSettings,
  updatePrivacySettings,
  updateGameplaySettings,
  updateDisplaySettings,
  resetSettings,
  exportSettings,
} from '../services/settingsService';
import { resetAllStats } from '../services/statsService';
import { auth } from '../services/firebase';
import '../styles/Settings.css';

const Settings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('appearance');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userSettings = await getUserSettings(currentUser.uid);
        setSettings(userSettings);
        setLoading(false);
      } else {
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleThemeChange = async (theme) => {
    if (!user) return;

    setSaving(true);
    const success = await updateTheme(user.uid, theme);

    if (success) {
      setSettings({ ...settings, theme });
      showMessage('Theme updated successfully!');
    } else {
      showMessage('Failed to update theme', 'error');
    }

    setSaving(false);
  };

  const handleNotificationChange = async (key, value) => {
    if (!user) return;

    setSaving(true);
    const updatedNotifications = { ...settings.notifications, [key]: value };
    const success = await updateNotificationSettings(user.uid, updatedNotifications);

    if (success) {
      setSettings({ ...settings, notifications: updatedNotifications });
      showMessage('Notification settings updated!');
    } else {
      showMessage('Failed to update notifications', 'error');
    }

    setSaving(false);
  };

  const handlePrivacyChange = async (key, value) => {
    if (!user) return;

    setSaving(true);
    const updatedPrivacy = { ...settings.privacy, [key]: value };
    const success = await updatePrivacySettings(user.uid, updatedPrivacy);

    if (success) {
      setSettings({ ...settings, privacy: updatedPrivacy });
      showMessage('Privacy settings updated!');
    } else {
      showMessage('Failed to update privacy settings', 'error');
    }

    setSaving(false);
  };

  const handleGameplayChange = async (key, value) => {
    if (!user) return;

    setSaving(true);
    const updatedGameplay = { ...settings.gameplay, [key]: value };
    const success = await updateGameplaySettings(user.uid, updatedGameplay);

    if (success) {
      setSettings({ ...settings, gameplay: updatedGameplay });
      showMessage('Gameplay settings updated!');
    } else {
      showMessage('Failed to update gameplay settings', 'error');
    }

    setSaving(false);
  };

  const handleDisplayChange = async (key, value) => {
    if (!user) return;

    setSaving(true);
    const updatedDisplay = { ...settings.display, [key]: value };
    const success = await updateDisplaySettings(user.uid, updatedDisplay);

    if (success) {
      setSettings({ ...settings, display: updatedDisplay });
      showMessage('Display settings updated!');
    } else {
      showMessage('Failed to update display settings', 'error');
    }

    setSaving(false);
  };

  const handleResetSettings = async () => {
    if (!user) return;

    const confirmed = window.confirm(
      'Are you sure you want to reset all settings to default? This action cannot be undone.'
    );

    if (!confirmed) return;

    setSaving(true);
    const success = await resetSettings(user.uid);

    if (success) {
      const userSettings = await getUserSettings(user.uid);
      setSettings(userSettings);
      showMessage('Settings reset to default!');
    } else {
      showMessage('Failed to reset settings', 'error');
    }

    setSaving(false);
  };

  const handleExportSettings = async () => {
    if (!user) return;

    const result = await exportSettings(user.uid);

    if (result) {
      const link = document.createElement('a');
      link.href = result.url;
      link.download = `ragebait-settings-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(result.url);

      showMessage('Settings exported successfully!');
    } else {
      showMessage('Failed to export settings', 'error');
    }
  };

  const handleResetStats = async () => {
    if (!user) return;

    const confirmed = window.confirm(
      'Are you sure you want to reset ALL your stats and game history? This action cannot be undone!'
    );

    if (!confirmed) return;

    setSaving(true);
    const success = await resetAllStats(user.uid);

    if (success) {
      showMessage('Stats and game history reset successfully!');
      // Refresh the page to show zeroed stats
      window.location.reload();
    } else {
      showMessage('Failed to reset stats', 'error');
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-message">Loading settings...</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="settings-container">
        <div className="error-message">Failed to load settings</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>⚙️ Settings</h1>
        <p>Customize your RageBait Studios experience</p>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-layout">
        {/* Tabs Navigation */}
        <div className="settings-tabs">
          <button
            className={`tab ${activeTab === 'appearance' ? 'active' : ''}`}
            onClick={() => setActiveTab('appearance')}
          >
            🎨 Appearance
          </button>
          <button
            className={`tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            🔔 Notifications
          </button>
          <button
            className={`tab ${activeTab === 'privacy' ? 'active' : ''}`}
            onClick={() => setActiveTab('privacy')}
          >
            🔒 Privacy
          </button>
          <button
            className={`tab ${activeTab === 'gameplay' ? 'active' : ''}`}
            onClick={() => setActiveTab('gameplay')}
          >
            🎮 Gameplay
          </button>
          <button
            className={`tab ${activeTab === 'display' ? 'active' : ''}`}
            onClick={() => setActiveTab('display')}
          >
            💻 Display
          </button>
        </div>

        {/* Settings Content */}
        <div className="settings-content">
          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="settings-section">
              <h2>Appearance</h2>
              <p className="section-description">
                Customize the look and feel of your application
              </p>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Theme</label>
                  <p className="setting-description">Choose your preferred color scheme</p>
                </div>
                <div className="theme-selector">
                  <button
                    className={`theme-option ${settings.theme === 'light' ? 'active' : ''}`}
                    onClick={() => handleThemeChange('light')}
                    disabled={saving}
                  >
                    ☀️ Light
                  </button>
                  <button
                    className={`theme-option ${settings.theme === 'dark' ? 'active' : ''}`}
                    onClick={() => handleThemeChange('dark')}
                    disabled={saving}
                  >
                    🌙 Dark
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="settings-section">
              <h2>Notifications</h2>
              <p className="section-description">
                Control which notifications you receive
              </p>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Sound Effects</label>
                  <p className="setting-description">Play notification sounds</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.notifications.sound}
                    onChange={(e) => handleNotificationChange('sound', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Desktop Notifications</label>
                  <p className="setting-description">Show browser notifications</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.notifications.desktop}
                    onChange={(e) => handleNotificationChange('desktop', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Friend Requests</label>
                  <p className="setting-description">Get notified of new friend requests</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.notifications.friendRequests}
                    onChange={(e) => handleNotificationChange('friendRequests', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Game Invites</label>
                  <p className="setting-description">Get notified of game invitations</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.notifications.gameInvites}
                    onChange={(e) => handleNotificationChange('gameInvites', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Messages</label>
                  <p className="setting-description">Get notified of new messages</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.notifications.messages}
                    onChange={(e) => handleNotificationChange('messages', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Game Events</label>
                  <p className="setting-description">Get notified of game starts and turns</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.notifications.gameEvents}
                    onChange={(e) => handleNotificationChange('gameEvents', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          )}

          {/* Privacy Tab */}
          {activeTab === 'privacy' && (
            <div className="settings-section">
              <h2>Privacy</h2>
              <p className="section-description">
                Manage your privacy and visibility settings
              </p>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Show Online Status</label>
                  <p className="setting-description">Let others see when you're online</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.privacy.showOnlineStatus}
                    onChange={(e) => handlePrivacyChange('showOnlineStatus', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Allow Friend Requests</label>
                  <p className="setting-description">Let others send you friend requests</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.privacy.allowFriendRequests}
                    onChange={(e) => handlePrivacyChange('allowFriendRequests', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Show Game History</label>
                  <p className="setting-description">Let others view your game history</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.privacy.showGameHistory}
                    onChange={(e) => handlePrivacyChange('showGameHistory', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          )}

          {/* Gameplay Tab */}
          {activeTab === 'gameplay' && (
            <div className="settings-section">
              <h2>Gameplay</h2>
              <p className="section-description">
                Customize your gameplay experience
              </p>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Auto Rematch</label>
                  <p className="setting-description">Automatically accept rematch requests</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.gameplay.autoRematch}
                    onChange={(e) => handleGameplayChange('autoRematch', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Show Hints</label>
                  <p className="setting-description">Display helpful hints during games</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.gameplay.showHints}
                    onChange={(e) => handleGameplayChange('showHints', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Sound Effects</label>
                  <p className="setting-description">Play game sound effects</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.gameplay.soundEffects}
                    onChange={(e) => handleGameplayChange('soundEffects', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Background Music</label>
                  <p className="setting-description">Play background music during games</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.gameplay.backgroundMusic}
                    onChange={(e) => handleGameplayChange('backgroundMusic', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Animation Speed</label>
                  <p className="setting-description">Control animation speed during games</p>
                </div>
                <div className="select-group">
                  <select
                    value={settings.gameplay.animationSpeed}
                    onChange={(e) => handleGameplayChange('animationSpeed', e.target.value)}
                    disabled={saving}
                  >
                    <option value="slow">Slow</option>
                    <option value="normal">Normal</option>
                    <option value="fast">Fast</option>
                  </select>
                </div>
              </div>

              <div className="divider"></div>

              <div className="danger-zone">
                <h3>Danger Zone</h3>
                <p className="danger-zone-description">
                  These actions are permanent and cannot be undone!
                </p>
                <button
                  className="danger-button"
                  onClick={handleResetStats}
                  disabled={saving}
                >
                  {saving ? 'Resetting...' : 'Reset All Stats & History'}
                </button>
                <p className="danger-warning">
                  ⚠️ This will permanently delete all your game stats, win/loss records, and game history.
                </p>
              </div>
            </div>
          )}

          {/* Display Tab */}
          {activeTab === 'display' && (
            <div className="settings-section">
              <h2>Display</h2>
              <p className="section-description">
                Adjust display and accessibility settings
              </p>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Time Format</label>
                  <p className="setting-description">Choose 12-hour or 24-hour time</p>
                </div>
                <div className="select-group">
                  <select
                    value={settings.display.timeFormat}
                    onChange={(e) => handleDisplayChange('timeFormat', e.target.value)}
                    disabled={saving}
                  >
                    <option value="12h">12-hour</option>
                    <option value="24h">24-hour</option>
                  </select>
                </div>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label>Reduced Motion</label>
                  <p className="setting-description">Minimize animations for accessibility</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={settings.display.reducedMotion}
                    onChange={(e) => handleDisplayChange('reducedMotion', e.target.checked)}
                    disabled={saving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="settings-actions">
            <button className="export-button" onClick={handleExportSettings} disabled={saving}>
              📥 Export Settings
            </button>
            <button className="reset-button" onClick={handleResetSettings} disabled={saving}>
              🔄 Reset to Default
            </button>
          </div>
        </div>
      </div>

      <div className="back-button-container">
        <button className="primary-back-button" onClick={() => navigate('/profile')} disabled={saving}>
          <span className="icon">👤</span>
          Back to Profile
        </button>
      </div>
    </div>
  );
};

export default Settings;
